import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { venuesAPI } from '../services/api';
import { formatCurrency } from '../utils/currency';
import { Plus, MapPin, Edit, Trash2, Clock, Maximize2, Users, Upload, X, Link, DollarSign, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import usePermissions from '../hooks/usePermissions';

// Cache key for venues
const VENUES_CACHE_KEY = 'myvenuemate_venues_cache';
const VENUES_CACHE_TIMESTAMP = 'myvenuemate_venues_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Get cached venues
const getCachedVenues = () => {
  try {
    const cached = localStorage.getItem(VENUES_CACHE_KEY);
    const timestamp = localStorage.getItem(VENUES_CACHE_TIMESTAMP);
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < CACHE_DURATION) {
        return JSON.parse(cached);
      }
    }
  } catch (e) {
    console.error('Cache read error:', e);
  }
  return null;
};

// Save venues to cache
const setCachedVenues = (venues) => {
  try {
    localStorage.setItem(VENUES_CACHE_KEY, JSON.stringify(venues));
    localStorage.setItem(VENUES_CACHE_TIMESTAMP, Date.now().toString());
  } catch (e) {
    console.error('Cache write error:', e);
  }
};

const Venues = () => {
  // Initialize with cached data for instant display
  const [venues, setVenues] = useState(() => getCachedVenues() || []);
  const [loading, setLoading] = useState(() => !getCachedVenues());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [venueToDelete, setVenueToDelete] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false); // Processing state for submit button
  const [fieldErrors, setFieldErrors] = useState({}); // Real-time validation errors
  const { toast } = useToast();
  
  // Permission checks
  const { canCreateVenues, canEditVenues, canDeleteVenues } = usePermissions();
  const canCreate = canCreateVenues();
  const canEdit = canEditVenues();
  const canDelete = canDeleteVenues();

  // Venue limit - fetched from backend (can be customized by master admin)
  const [venueLimit, setVenueLimit] = useState({ maxLimit: 4, isCustomLimit: false });
  const venueCount = venues.length;
  const canAddMoreVenues = venueCount < venueLimit.maxLimit && canCreate;
  const remainingVenues = venueLimit.maxLimit - venueCount;

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    capacity: '',
    location: '',
    pricePerDay: '',
    mapUrl: '',
    amenities: '',
    description: '',
    photo: null,
    photoPreview: ''
  });

  useEffect(() => {
    // Load venues and limit info in parallel
    const loadData = async () => {
      const hasCachedData = !!getCachedVenues();
      
      if (hasCachedData) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      
      try {
        // Fetch both in parallel for faster loading
        const [venuesData, limitInfo] = await Promise.all([
          venuesAPI.getAll(),
          venuesAPI.getLimitInfo()
        ]);
        
        const venuesList = venuesData || [];
        setVenues(venuesList);
        setCachedVenues(venuesList);
        
        if (limitInfo) {
          setVenueLimit({
            maxLimit: limitInfo.maxLimit || 4,
            isCustomLimit: limitInfo.isCustomLimit || false
          });
        }
      } catch (error) {
        if (!getCachedVenues()) {
          toast({
            title: 'Error',
            description: 'Failed to load venues. Please try again.',
            variant: 'destructive'
          });
          setVenues([]);
        }
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    };
    
    loadData();
  }, []);

  const loadVenueLimit = async () => {
    try {
      const limitInfo = await venuesAPI.getLimitInfo();
      if (limitInfo) {
        setVenueLimit({
          maxLimit: limitInfo.maxLimit || 4,
          isCustomLimit: limitInfo.isCustomLimit || false
        });
      }
    } catch (error) {
      console.error('Failed to load venue limit:', error);
    }
  };

  const loadVenues = async (isBackgroundRefresh = false) => {
    if (isBackgroundRefresh) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const data = await venuesAPI.getAll();
      const venuesList = data || [];
      setVenues(venuesList);
      setCachedVenues(venuesList); // Update cache
    } catch (error) {
      console.error('Failed to load venues:', error);
      // Only show error toast if we don't have cached data
      if (!getCachedVenues()) {
        toast({
          title: 'Error',
          description: 'Failed to load venues. Please try again.',
          variant: 'destructive'
        });
      }
      if (!isBackgroundRefresh) {
        setVenues([]);
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Manual refresh function
  const handleRefresh = () => {
    loadVenues(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting) return;
    
    // Permission check
    if (editingVenue && !canEdit) {
      toast({ 
        title: '❌ Permission Denied', 
        description: 'You do not have permission to edit venues.',
        variant: 'destructive'
      });
      return;
    }
    if (!editingVenue && !canCreate) {
      toast({ 
        title: '❌ Permission Denied', 
        description: 'You do not have permission to create venues.',
        variant: 'destructive'
      });
      return;
    }
    
    // Check for duplicate venue name (case-insensitive)
    const venueName = formData.name.trim().toLowerCase();
    const duplicateVenue = venues.find(v => 
      v.name.toLowerCase() === venueName && 
      (!editingVenue || v.id !== editingVenue.id)
    );

    if (duplicateVenue) {
      toast({ 
        title: '❌ Duplicate Venue Name', 
        description: `A venue with the name "${formData.name}" already exists. Please use a different name.`,
        variant: 'destructive'
      });
      return;
    }

    const venueData = {
      name: formData.name,
      type: formData.type,
      capacity: Number(formData.capacity),
      location: formData.location,
      pricePerDay: Number(formData.pricePerDay) || 0,
      amenities: formData.amenities.split(',').map(a => a.trim()).filter(a => a),
      description: formData.description,
      image: formData.photoPreview || formData.image || null,
      mapUrl: formData.mapUrl || null,
      status: 'active'
    };

    setIsSubmitting(true); // Start processing
    
    try {
      let updatedVenue;
      if (editingVenue) {
        updatedVenue = await venuesAPI.update(editingVenue.id, venueData);
        // Optimistic update - update local state immediately
        setVenues(prev => prev.map(v => v.id === editingVenue.id ? { ...v, ...venueData } : v));
        toast({ title: '✅ Success', description: 'Venue updated successfully' });
      } else {
        updatedVenue = await venuesAPI.create(venueData);
        // Optimistic update - add to local state immediately
        if (updatedVenue) {
          setVenues(prev => [...prev, updatedVenue]);
          setCachedVenues([...venues, updatedVenue]);
        }
        toast({ title: '✅ Success', description: 'Venue created successfully' });
      }
      
      resetForm();
      setDialogOpen(false);
      
      // Background refresh to ensure data consistency (non-blocking)
      loadVenues(true);
    } catch (error) {
      console.error('Failed to save venue:', error);
      toast({
        title: '❌ Error',
        description: error.message || 'Failed to save venue. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false); // End processing
    }
  };

  const handleEdit = (venue) => {
    if (!canEdit) {
      toast({ 
        title: '❌ Permission Denied', 
        description: 'You do not have permission to edit venues.',
        variant: 'destructive'
      });
      return;
    }
    setEditingVenue(venue);
    setFormData({
      ...venue,
      amenities: venue.amenities.join(', '),
      photoPreview: venue.image || '',
      photo: null
    });
    setPhotoPreview(venue.image || '');
    setDialogOpen(true);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({ 
          title: 'Error', 
          description: 'Please select an image file',
          variant: 'destructive'
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({ 
          title: 'Error', 
          description: 'Image size should be less than 5MB',
          variant: 'destructive'
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setFormData({
          ...formData,
          photo: file,
          photoPreview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoPreview('');
    setFormData({
      ...formData,
      photo: null,
      photoPreview: ''
    });
  };

  const handleDeleteClick = (venue) => {
    if (!canDelete) {
      toast({ 
        title: '❌ Permission Denied', 
        description: 'You do not have permission to delete venues.',
        variant: 'destructive'
      });
      return;
    }
    setVenueToDelete(venue);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete) {
      toast({ 
        title: '❌ Permission Denied', 
        description: 'You do not have permission to delete venues.',
        variant: 'destructive'
      });
      return;
    }
    if (venueToDelete) {
      try {
        await venuesAPI.delete(venueToDelete.id);
        toast({ title: 'Success', description: 'Venue deleted successfully' });
        await loadVenues(); // Reload venues from backend
        setDeleteConfirmOpen(false);
        setVenueToDelete(null);
      } catch (error) {
        console.error('Failed to delete venue:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete venue. Please try again.',
          variant: 'destructive'
        });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      capacity: '',
      location: '',
      pricePerDay: '',
      mapUrl: '',
      amenities: '',
      description: '',
      photo: null,
      photoPreview: ''
    });
    setPhotoPreview('');
    setEditingVenue(null);
    setFieldErrors({}); // Clear validation errors
  };

  // Real-time field validation
  const validateField = (fieldName, value) => {
    let error = '';
    
    switch (fieldName) {
      case 'name':
        if (!value || value.trim() === '') {
          error = 'Venue name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        }
        break;
        
      case 'type':
        if (!value || value.trim() === '') {
          error = 'Venue type is required';
        }
        break;
        
      case 'location':
        if (!value || value.trim() === '') {
          error = 'Location is required';
        }
        break;
        
      case 'capacity':
        if (!value || value === '') {
          error = 'Capacity is required';
        } else {
          const num = Number(value);
          if (isNaN(num) || num <= 0 || !Number.isInteger(num)) {
            error = 'Must be a positive whole number';
          }
        }
        break;
        
      case 'pricePerDay':
        if (value && value !== '') {
          const num = Number(value);
          if (isNaN(num) || num < 0) {
            error = 'Cannot be negative';
          }
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  // Handle field change with real-time validation
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    // Validate the field
    const error = validateField(fieldName, value);
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  // Validate field on blur
  const handleFieldBlur = (fieldName) => {
    const error = validateField(fieldName, formData[fieldName]);
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 dark:bg-green-900/30 text-green-800 border-green-200',
      inactive: 'bg-red-100 dark:bg-red-900/30 text-red-800 border-red-200',
      maintenance: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 border-orange-200'
    };
    return colors[status] || colors.active;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-foreground">Venues</h1>
            <p className="text-gray-600 dark:text-muted-foreground mt-1">
              Manage all venue properties
              {isRefreshing && <span className="text-emerald-600 text-sm ml-2">• Syncing...</span>}
            </p>
            {/* Venue limit indicator */}
            <div className="flex items-center gap-2 mt-2">
              <Badge 
                variant={canAddMoreVenues ? "secondary" : "destructive"}
                className={`text-xs ${canAddMoreVenues ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700' : 'bg-red-100 dark:bg-red-900/30 text-red-700'}`}
              >
                {venueCount} / {venueLimit.maxLimit} Venues
                {venueLimit.isCustomLimit && <span className="ml-1">★</span>}
              </Badge>
              {!canAddMoreVenues && (
                <span className="text-xs text-red-600">• Maximum limit reached</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading || isRefreshing}
            className="hover:bg-emerald-50 dark:bg-emerald-900/20"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading || isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Syncing' : 'Refresh'}
          </Button>
          {canCreate && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className={`shadow-md ${canAddMoreVenues 
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700' 
                  : 'bg-gray-400 cursor-not-allowed'}`}
                disabled={!canAddMoreVenues}
                title={!canAddMoreVenues ? `Maximum ${venueLimit.maxLimit} venues allowed` : ''}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Venue {!canAddMoreVenues && '(Limit Reached)'}
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle>{editingVenue ? 'Edit Venue' : 'Add New Venue'}</DialogTitle>
                <DialogDescription>
                {editingVenue ? 'Update venue information' : `Add a new venue to your portfolio (${remainingVenues} remaining of ${venueLimit.maxLimit})`}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className={fieldErrors.name ? 'text-red-600' : ''}>Venue Name * <span className="text-xs text-gray-400 dark:text-muted-foreground">(max 25 chars)</span></Label>
                  <Input 
                    id="name" 
                    value={formData.name} 
                    onChange={(e) => handleFieldChange('name', e.target.value.slice(0, 25))}
                    onBlur={() => handleFieldBlur('name')}
                    required 
                    maxLength={25}
                    className={fieldErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}
                  />
                  <div className="flex justify-between">
                    {fieldErrors.name ? (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {fieldErrors.name}
                      </p>
                    ) : <span />}
                    <span className={`text-xs ${formData.name.length >= 25 ? 'text-orange-500' : 'text-gray-400 dark:text-muted-foreground'}`}>
                      {formData.name.length}/25
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type" className={fieldErrors.type ? 'text-red-600' : ''}>Type</Label>
                  <Input 
                    id="type" 
                    value={formData.type} 
                    onChange={(e) => handleFieldChange('type', e.target.value)}
                    onBlur={() => handleFieldBlur('type')}
                    placeholder="e.g., Lawn, AC Hall, Enclosed, Banquet, Open Air" 
                    className={fieldErrors.type ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}
                  />
                  {fieldErrors.type && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {fieldErrors.type}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">Optional - Examples: Lawn, AC, Enclosed, Banquet, Open Air, etc.</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location" className={fieldErrors.location ? 'text-red-600' : ''}>Location *</Label>
                <Input 
                  id="location" 
                  value={formData.location} 
                  onChange={(e) => handleFieldChange('location', e.target.value)}
                  onBlur={() => handleFieldBlur('location')}
                  required 
                  placeholder="Full address" 
                  className={fieldErrors.location ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}
                />
                {fieldErrors.location && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {fieldErrors.location}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mapUrl">
                  <div className="flex items-center gap-2">
                    <Link className="w-4 h-4" />
                    Google Maps URL
                  </div>
                </Label>
                <Input 
                  id="mapUrl" 
                  type="url"
                  value={formData.mapUrl} 
                  onChange={(e) => setFormData({...formData, mapUrl: e.target.value})} 
                  placeholder="https://maps.google.com/..." 
                />
                <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">
                  Paste the Google Maps link for this venue location
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="capacity" className={fieldErrors.capacity ? 'text-red-600' : ''}>Capacity *</Label>
                  <Input 
                    id="capacity" 
                    type="number" 
                    value={formData.capacity} 
                    onChange={(e) => handleFieldChange('capacity', e.target.value)}
                    onBlur={() => handleFieldBlur('capacity')}
                    required 
                    placeholder="Number of guests" 
                    className={fieldErrors.capacity ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}
                  />
                  {fieldErrors.capacity && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {fieldErrors.capacity}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pricePerDay" className={fieldErrors.pricePerDay ? 'text-red-600' : ''}>Price Per Day</Label>
                  <Input 
                    id="pricePerDay" 
                    type="number" 
                    value={formData.pricePerDay} 
                    onChange={(e) => handleFieldChange('pricePerDay', e.target.value)}
                    onBlur={() => handleFieldBlur('pricePerDay')}
                    placeholder="e.g., 50000" 
                    className={fieldErrors.pricePerDay ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-900/20' : ''}
                  />
                  {fieldErrors.pricePerDay && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {fieldErrors.pricePerDay}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amenities">Amenities (comma-separated)</Label>
                <Input id="amenities" value={formData.amenities} onChange={(e) => setFormData({...formData, amenities: e.target.value})} placeholder="Stage, Sound System, Lighting, Parking, etc." />
              </div>

              {/* Photo Upload Section */}
              <div className="space-y-2">
                <Label>Venue Photo</Label>
                <div className="border-2 border-dashed border-gray-300 dark:border-border rounded-lg p-4 hover:border-emerald-400 transition-colors">
                  {photoPreview ? (
                    <div className="relative">
                      <img 
                        src={photoPreview} 
                        alt="Venue preview" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2"
                        onClick={handleRemovePhoto}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-12 h-12 text-gray-400 dark:text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-gray-600 dark:text-muted-foreground mb-2">Click to upload venue photo</p>
                      <p className="text-xs text-gray-500 dark:text-muted-foreground mb-3">PNG, JPG up to 5MB</p>
                      <input
                        type="file"
                        id="photo"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('photo').click()}
                      >
                        Select Photo
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} placeholder="Describe the venue, its features, and what makes it special..." />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingVenue ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>{editingVenue ? 'Update' : 'Create'} Venue</>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      {/* Venues Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && venues.length === 0 ? (
          // Loading Skeletons - only show when no cached data
          [...Array(6)].map((_, index) => (
            <Card key={index} className="border-0 shadow-md overflow-hidden animate-pulse">
              <div className="h-48 bg-gradient-to-br from-gray-200 to-gray-300" />
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <div className="h-6 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
                <div className="flex space-x-2 pt-3">
                  <div className="h-9 bg-gray-200 rounded flex-1" />
                  <div className="h-9 bg-gray-200 rounded flex-1" />
                </div>
              </CardContent>
            </Card>
          ))
        ) : venues.length > 0 ? (
          venues.map((venue) => (
            <Card key={venue.id} className="border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
              <div className="relative h-48 overflow-hidden bg-gradient-to-br from-emerald-100 to-teal-100">
                {venue.image ? (
                  <img 
                    src={venue.image} 
                    alt={venue.name} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <MapPin className="w-16 h-16 text-emerald-400" />
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <Badge className={getStatusColor(venue.status)}>
                    {venue.status}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-foreground">{venue.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">{venue.type}</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-700 dark:text-foreground">
                    <MapPin className="w-4 h-4 mr-2 text-emerald-600" />
                    <span className="truncate">{venue.location}</span>
                  </div>
                  {venue.mapUrl && (
                    <div className="flex items-center text-sm">
                      <Link className="w-4 h-4 mr-2 text-blue-600" />
                      <a 
                        href={venue.mapUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline truncate"
                      >
                        View on Google Maps
                      </a>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-700 dark:text-foreground">
                    <Users className="w-4 h-4 mr-2 text-teal-600" />
                    <span>Capacity: {venue.capacity} guests</span>
                  </div>
                  {venue.pricePerDay > 0 && (
                    <div className="flex items-center text-sm text-gray-700 dark:text-foreground">
                      <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                      <span>₹{venue.pricePerDay.toLocaleString()}/day</span>
                    </div>
                  )}
                </div>

                {venue.description && (
                  <p className="text-sm text-gray-600 dark:text-muted-foreground line-clamp-2">{venue.description}</p>
                )}

                {venue.amenities && venue.amenities.length > 0 && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-gray-600 dark:text-muted-foreground mb-2">Amenities:</p>
                    <div className="flex flex-wrap gap-1">
                      {venue.amenities.slice(0, 4).map((amenity, index) => (
                        <Badge key={index} variant="outline" className="text-xs bg-gray-50 dark:bg-secondary">
                          {amenity}
                        </Badge>
                      ))}
                      {venue.amenities.length > 4 && (
                        <Badge variant="outline" className="text-xs bg-gray-50 dark:bg-secondary">
                          +{venue.amenities.length - 4} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex space-x-2 pt-3">
                  {canEdit && (
                    <Button
                      onClick={() => handleEdit(venue)}
                      variant="outline"
                      size="sm"
                      className="flex-1 hover:bg-emerald-50 dark:bg-emerald-900/20 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      onClick={() => handleDeleteClick(venue)}
                      variant="outline"
                      size="sm"
                      className="flex-1 hover:bg-red-50 dark:bg-red-900/20 hover:border-red-300 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                  {!canEdit && !canDelete && (
                    <p className="text-xs text-gray-400 dark:text-muted-foreground italic">View only</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="border-0 shadow-md col-span-3">
            <CardContent className="p-12 text-center">
              <MapPin className="w-16 h-16 text-gray-400 dark:text-muted-foreground mx-auto mb-4" />
              <p className="text-gray-600 dark:text-muted-foreground text-lg">No venues found</p>
              <p className="text-gray-500 dark:text-muted-foreground text-sm mt-2">Try adjusting your filters or add a new venue</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        title="Delete Venue?"
        description="This will permanently delete this venue. All bookings associated with this venue will need to be reassigned or cancelled."
        itemName={venueToDelete?.name}
        confirmText="Delete Venue"
      />
    </div>
  );
};

export default Venues;
